import { EmployeeSchedule, employeeScheduleToWorkingPattern } from '@/helpers/workingPattern/employeeSchedule';
import { format } from 'date-fns';
import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UseQueryExecute } from 'urql';
import { Button } from '../../../../../components/shared/button/Button';
import { TSCard } from '../../../../../components/shared/card/Card';
import { DateInput } from '../../../../../components/shared/date/DateInput';
import { ConfirmDialog } from '../../../../../components/shared/modal/ConfirmDialog';
import { handleMutationResult } from '../../../../../helpers/toastHelper';
import { useContract } from '../../../../../services/contract/useContract';
import {
  useGetMeQuery,
  useUpdateWorkingPatternDirectlyMutation,
  useUpdateWorkingPatternMutation
} from '../../../../../services/gql/graphql.generated';
import strings from '../../../team.translations';
import { ScheduleGrid } from './ScheduleGrid';
import { useScheduleEditor } from './useScheduleEditor';

interface ScheduleEditFormProps {
  userId: string;
  userFirstName: string;
  contractTypeId?: string;
  initSchedule: EmployeeSchedule[];
  refetchSchedule: UseQueryExecute;
}

export const ScheduleEditForm: FC<ScheduleEditFormProps> = ({
  userId,
  userFirstName,
  contractTypeId,
  initSchedule,
  refetchSchedule
}) => {
  const contract = useContract(contractTypeId);
  const {
    schedule,
    effectiveFrom,
    setEffectiveFrom,
    getDaySchedule,
    canAddSplitPeriod,
    onCheck,
    onStartChange,
    onEndChange,
    onAdd,
    onDelete,
    validationErrors
  } = useScheduleEditor(initSchedule, contract);

  const [updatePatternRes, updatePattern] = useUpdateWorkingPatternMutation();
  const [updatePatternDirectlyRes, updatePatternDirectly] = useUpdateWorkingPatternDirectlyMutation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);
  const navigate = useNavigate();

  const [{ data: userData, error: userError }] = useGetMeQuery();
  const isSelf = userData?.myEmployee?.identityId === userId;
  const updateResult = isSelf ? updatePatternRes : updatePatternDirectlyRes;

  const onSubmit = () => {
    setHasAttemptedSubmit(true);

    if (validationErrors.length > 0) {
      return;
    }

    setModalOpen(true);
  };

  const onSaveNavigate = () => {
    if (isSelf) {
      navigate('/ess/my-schedule/change-requested');
      return;
    }
    navigate(-1);
  };

  const onCancel = () => {
    navigate(-1);
  };

  const onSave = async () => {
    if (!effectiveFrom) {
      return;
    }

    const args = {
      employeeId: userId,
      patterns: employeeScheduleToWorkingPattern(schedule),
      effectiveFrom: format(effectiveFrom, 'yyyy-MM-dd')
    };
    const result = isSelf ? await updatePattern({ args }) : await updatePatternDirectly({ args });

    await refetchSchedule({ requestPolicy: 'network-only' });
    setTimeout(
      () =>
        handleMutationResult(result, {
          successMessage: isSelf ? undefined : strings.editSchedule.scheduleUpdated,
          onSuccess: onSaveNavigate
        }),
      100
    );
  };

  const minEffectiveDate = new Date();
  minEffectiveDate.setHours(0, 0, 0, 0);

  return (
    <>
      <TSCard className="space-y-5 max-w-3xl mx-auto p-5">
        <ScheduleGrid
          getDaySchedule={getDaySchedule}
          canAddSplitPeriod={canAddSplitPeriod}
          onCheck={onCheck}
          onStartChange={onStartChange}
          onEndChange={onEndChange}
          onAdd={onAdd}
          onDelete={onDelete}
        />

        <div className="pt-2">
          <p className="text-sm font-medium mb-2">{strings.editSchedule.effectiveFrom}</p>
          <DateInput value={effectiveFrom} onChange={setEffectiveFrom} minDate={minEffectiveDate} />
        </div>

        {hasAttemptedSubmit && validationErrors.length > 0 ? (
          <div className="space-y-1">
            {validationErrors.map(error => (
              <p className="text-sm text-red-500 leading-5" key={error}>
                {error}
              </p>
            ))}
          </div>
        ) : null}
        <ConfirmDialog
          title={strings.editSchedule.editTitle}
          confirmLabel={strings.editSchedule.confirm}
          cancelLabel={strings.common.cancel}
          open={modalOpen}
          onConfirm={onSave}
          onClose={() => setModalOpen(false)}
          loading={updateResult.fetching}
        >
          {isSelf
            ? strings.editSchedule.selfModalText(effectiveFrom ? format(effectiveFrom, 'dd/MM/yyyy') : '')
            : strings.editSchedule.modalText(userFirstName, effectiveFrom ? format(effectiveFrom, 'dd/MM/yyyy') : '')}
        </ConfirmDialog>
      </TSCard>
      <div className="space-y-5 max-w-3xl mx-auto pt-5 pl-5">
        <div className="flex w-full items-center justify-end gap-5">
          <Button onClick={onCancel}>{strings.common.cancel}</Button>
          <Button primary onClick={onSubmit}>
            {isSelf ? strings.common.request : strings.common.save}
          </Button>
        </div>
      </div>
    </>
  );
};
